import {type MouseEvent, useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {type NotificationDto} from '@onroadvantage/optix-safeandsound-openapi';

import {useNotificationList} from './useNotificationList';
import {useNotificationMutations} from './useNotificationMutations';
import {useUnreadNotificationCount} from './useUnreadNotificationCount';
import {replaceParams, routes} from '../../constants/routes';

const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

// Backend linkUrls (e.g. /courses/{id}) do not match the app's portal-prefixed
// routes, so internal targets are derived from entityType/entityId instead.
// For ENROLLMENT notifications entityId is the enrollmentId, not the courseId;
// the courseId is only available via metadata.
const getMetadataCourseId = (notification: NotificationDto): string | null => {
  const courseId = notification.metadata?.courseId;
  if (typeof courseId === 'string' && courseId !== '') {
    return courseId;
  }
  return null;
};

const resolveInternalRoute = (notification: NotificationDto): string | null => {
  if (notification.entityType === 'COURSE' && notification.entityId != null) {
    return replaceParams(routes.learnerPortal.viewCourse, {
      courseId: notification.entityId,
    });
  }
  if (notification.entityType === 'ENROLLMENT') {
    const courseId = getMetadataCourseId(notification);
    if (courseId != null) {
      return replaceParams(routes.learnerPortal.viewCourse, {courseId});
    }
    return routes.learnerPortal.courses;
  }
  if (notification.entityType === 'CERTIFICATE') {
    return routes.learnerPortal.certificates;
  }
  if (
    notification.linkUrl != null &&
    notification.linkUrl !== '' &&
    !ABSOLUTE_URL_PATTERN.test(notification.linkUrl)
  ) {
    return notification.linkUrl;
  }
  return null;
};

export const hasNotificationLink = (notification: NotificationDto): boolean => {
  if (
    notification.linkUrl != null &&
    ABSOLUTE_URL_PATTERN.test(notification.linkUrl)
  ) {
    return true;
  }
  return resolveInternalRoute(notification) != null;
};

export interface UseNotificationsMenuReturn {
  anchorEl: HTMLElement | null;
  open: boolean;
  count: number;
  notifications: NotificationDto[];
  loading: boolean;
  isError: boolean;
  isMarkingAllRead: boolean;
  onOpen: (event: MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  onMarkAllRead: () => void;
  onArchive: (id: string) => void;
  onNotificationClick: (notification: NotificationDto) => void;
}

export const useNotificationsMenu = (): UseNotificationsMenuReturn => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const {count} = useUnreadNotificationCount();
  const {notifications, loading, isError} = useNotificationList(open);
  const {markRead, markAllRead, archive, isMarkingAllRead} =
    useNotificationMutations();

  const onOpen = useCallback((event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  }, []);

  const onClose = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const onNotificationClick = useCallback(
    (notification: NotificationDto): void => {
      if (notification.readAt == null) {
        markRead(notification.id);
      }
      if (
        notification.linkUrl != null &&
        ABSOLUTE_URL_PATTERN.test(notification.linkUrl)
      ) {
        window.open(notification.linkUrl, '_blank', 'noopener,noreferrer');
      } else {
        const route = resolveInternalRoute(notification);
        if (route != null) {
          navigate(route);
        }
      }
      setAnchorEl(null);
    },
    [markRead, navigate],
  );

  return {
    anchorEl,
    open,
    count,
    notifications,
    loading,
    isError,
    isMarkingAllRead,
    onOpen,
    onClose,
    onMarkAllRead: markAllRead,
    onArchive: archive,
    onNotificationClick,
  };
};
